import { PropsWithChildren } from "react";

import {
  isSharesProject,
  type BondsProject,
  type Project,
  type SharesProject,
} from "@tudigo-monorepo/core-tudigo-api-models";
import { cn, themeColors } from "@tudigo-monorepo/core-tudigo-theme";
import { useTranslation } from "@tudigo-monorepo/core-tudigo-translations";
import { useUserBusiness } from "@tudigo-monorepo/tudigo-session";
import {
  BadgeRebrand,
  Icon,
  Tooltip,
} from "@tudigo-monorepo/web-tudigo-components";

import { getProjectInternalRateOfReturn } from "../utils/get-project-internal-rate-of-return";

const Setting = (props: {
  label: string;
  value: string | number | string[] | number[];
  tooltipDescription?: string;
}) => {
  const { label, value } = props;
  const { isQualifiedOrWma } = useUserBusiness();

  return (
    <li className="flex min-h-5 flex-wrap items-center gap-2">
      <p className="text-black-900 text-xs">{label}</p>
      <Tooltip placement="top" description={props.tooltipDescription}>
        <Icon name="Info" size="XS" primaryColor={themeColors["black-900"]} />
      </Tooltip>
      {!isQualifiedOrWma ? (
        <Icon
          name="Cadenas"
          size="XS"
          primaryColor={themeColors["black-primary"]}
        />
      ) : (
        <p className="text-black-primary text-xs font-semibold">{value}</p>
      )}
    </li>
  );
};

const MediumInPercentSetting = (props: {
  project: SharesProject;
  isLimitedAccess?: boolean;
}) => {
  const { t } = useTranslation();
  const exitHorizonInMonths =
    props.project?.sharesMechanicSettings?.exitHorizon?.maxInMonths;

  const targetedYield = props.project?.sharesMechanicSettings?.targetedYield;

  if (!targetedYield || !exitHorizonInMonths) {
    return null;
  }

  const exitHorizonInYears = exitHorizonInMonths / 12;

  const displayExitHorizonInYears =
    exitHorizonInYears % 1 === 0
      ? Math.round(exitHorizonInYears)
      : exitHorizonInYears.toFixed(1);

  return (
    <Setting
      label={t("component.project_card.list.item.targeted_exit")}
      value={`x${
        targetedYield.mediumInPercent / 100
      } à ${displayExitHorizonInYears} ans`}
      tooltipDescription="Objectif d’après notre scénario défini par le management de la société"
    />
  );
};

const TaxExemptionMechanicsBadges = (props: { project: SharesProject }) => {
  const { t } = useTranslation();

  const { taxExemptionMechanics } = props.project;

  if (!taxExemptionMechanics || taxExemptionMechanics.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex flex-wrap gap-1">
        <div className="flex items-center gap-1">
          <p className="text-black-900 text-xs">
            {t("component.project_card.list.item.tax_exemption")}
          </p>
          <Tooltip
            placement="top"
            description={t(
              "component.project_card.list.item.tax_exemption.info",
            )}
          >
            <Icon
              name="Info"
              size="XS"
              primaryColor={themeColors["black-900"]}
            />
          </Tooltip>
        </div>

        {taxExemptionMechanics?.map((tax, index) => {
          if (tax === "JEIR" || tax === "JEIC") {
            const taxPercentage = tax === "JEIR" ? 50 : 30;

            return (
              <BadgeRebrand key={index} variant="primary">
                {t(`component.project_card.list.item.tax_exemption.${tax}`)}{" "}
                {taxPercentage}%
              </BadgeRebrand>
            );
          }

          if (tax === "IR") {
            const taxPercentage = 18;

            return (
              <BadgeRebrand key={index} variant="tertiary">
                {t(`component.project_card.list.item.tax_exemption.${tax}`)}{" "}
                {taxPercentage}%
              </BadgeRebrand>
            );
          }

          return (
            <BadgeRebrand key={index} variant="tertiary">
              {t(`component.project_card.list.item.tax_exemption.${tax}`)}
            </BadgeRebrand>
          );
        })}
      </div>
    </div>
  );
};

const SharesProjectCardContent = (props: { project: SharesProject }) => {
  const { project } = props;
  const { sharesMechanicSettings, status } = project;

  const { t } = useTranslation();

  const { targetedYield, buyPack, buyPackPeriodInMonth } =
    sharesMechanicSettings;

  const internalRateOfReturn = getProjectInternalRateOfReturn(project);

  return (
    <>
      {status !== "prefinished" && status !== "finished" && (
        <>
          {buyPack && (
            <>
              {!!internalRateOfReturn?.value && (
                <Setting
                  label={t(
                    "component.project_card.list.item.buy-pack.internal-rate-of-return",
                  )}
                  value={`${internalRateOfReturn.value / 100}%`}
                  tooltipDescription={t(
                    "component.project_card.list.item.buy-pack.internal-rate-of-return.tooltip",
                  )}
                />
              )}
              {buyPackPeriodInMonth > 0 && (
                <Setting
                  label={t(
                    "component.project_card.list.item.buy-pack.buy-pack-period-in-month",
                  )}
                  value={`${buyPackPeriodInMonth} mois`}
                  tooltipDescription={t(
                    "component.project_card.list.item.buy-pack.buy-pack-period-in-month.tooltip",
                  )}
                />
              )}
            </>
          )}
          {!!targetedYield?.mediumInPercent && (
            <MediumInPercentSetting project={project} />
          )}
          {!!project?.riskInPercent && (
            <Setting
              label={t("component.project_card.list.item.risk")}
              value={`${project.riskInPercent / 10} /10`}
              tooltipDescription="Le potentiel de rendement a pour corolaire un risque de perte totale ou partielle du capital investit. L’analyse financière proposée sur la page projet et dans la note d’investissement détaille le calcul effectué par nos analystes pour chaque levée."
            />
          )}
          {/* Impact Score is commented and not deleted on purpose */}
          {/* {project.impactScoreInPercent !== null &&
            project.impactScoreInPercent > 0 && (
              <Setting
                label={t("component.project_card.list.item.impact")}
                value={`${project.impactScoreInPercent / 10} /10`}
                tooltipDescription="Note d’impact environnementale et/ou sociétal, attribuée par nos analystes pour un projet."
              />
            )} */}
        </>
      )}
      <TaxExemptionMechanicsBadges project={project} />
    </>
  );
};

const BondsProjectCardContent = (props: { project: BondsProject }) => {
  const { project } = props;
  const { bondsMechanicSettings, status } = project;

  const { t } = useTranslation();

  const { annualInterestNoDecimalRate, durationInMonth, repaymentType } =
    bondsMechanicSettings;

  return (
    <>
      {status !== "prefinished" && status !== "finished" && (
        <>
          {!!project.riskInPercent && (
            <Setting
              label={t("component.project_card.list.item.risk")}
              value={`${project.riskInPercent / 10} /10`}
              tooltipDescription="Le potentiel de rendement a pour corolaire un risque de perte totale ou partielle du capital investit. L’analyse financière proposée sur la page projet et dans la note d’investissement détaille le calcul effectué par nos analystes pour chaque levée."
            />
          )}
          <Setting
            label={t("component.project_card.list.item.interest_rate")}
            value={`${annualInterestNoDecimalRate / 100} %`}
            tooltipDescription="C’est le pourcentage du montant emprunté qui rémunère les fonds prêtés."
          />
        </>
      )}
      <Setting
        label={t("component.project_card.list.item.duration")}
        value={`${durationInMonth} ${t(
          "component.project_card.list.item.months",
        )}`}
        tooltipDescription="Durée de l’investissement"
      />
      <Setting
        label={t("component.project_card.list.item.repayment")}
        value={t(`repayment_type.${repaymentType}`)}
        tooltipDescription="Fréquence de remboursement pour cet investissement."
      />
      {/* Impact Score is commented and not deleted on purpose */}
      {/* {project.impactScoreInPercent !== null &&
        project.impactScoreInPercent > 0 && (
          <Setting
            label={t("component.project_card.list.item.impact")}
            value={`${project.impactScoreInPercent / 10} /10`}
            tooltipDescription="Note d’impact environnementale et/ou sociétal, attribuée par nos analystes pour un projet."
          />
        )} */}
    </>
  );
};

type ProjectMechanicSettingsCardProps = {
  project: Project;
  className?: string;
};

const CardContainer = ({
  children,
  project,
  className,
}: PropsWithChildren<ProjectMechanicSettingsCardProps>) => {
  const { status } = project;

  const isVisible =
    isSharesProject(project) || !project.bondsMechanicSettings.isConvertible;

  const heightRules = {
    "min-h-[136px]": ["draft", "scheduled", "started"].includes(status),
    "min-h-[86px]": ["prefinished", "finished"].includes(status),
  };

  return (
    <div
      className={cn(
        "flex h-auto w-full flex-col",
        "mb-6 gap-2 px-4 py-4",
        isVisible && "bg-black-400 border-black-500 rounded-md border",
        { ...heightRules },
        className,
      )}
    >
      {children}
    </div>
  );
};

export const ProjectMechanicSettingsCard = ({
  project,
  className,
}: ProjectMechanicSettingsCardProps) => {
  return (
    <CardContainer project={project} className={className}>
      {isSharesProject(project) ? (
        <SharesProjectCardContent project={project} />
      ) : !project.bondsMechanicSettings.isConvertible ? (
        <BondsProjectCardContent project={project} />
      ) : null}
    </CardContainer>
  );
};
